import { Fragment, type ReactNode } from "react";
import { PanelRightOpen } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ConfigActivityRow } from "@/api/config-server-stages";
import type { ChangedItem } from "@/api/management";
import type { ActivityFiltersState } from "@/components/agents/configuration-server/stages/activityFilters";
import { activityDoneLabel } from "@/components/agents/configuration-server/stages/activityStatus";
import { SelectAllBar } from "@/components/agents/configuration-server/stages/BulkActions";
import {
  ApproveButton,
  DecidedPanelBody,
  DecidedRow,
  DecisionCheckbox,
  FILTER_ROW_CLASS,
  FILTER_SELECT_CLASS,
  FilterSearch,
  GhostIconButton,
  OutOfScopeButton,
} from "@/components/agents/configuration-server/stages/DecisionRows";
import { LoadMore } from "@/components/agents/configuration-server/stages/Paging";
import { countryName } from "@/lib/country-names";
import { cn } from "@/lib/utils";

export type ActivityKind = "sscui" | "fiori";
const GLOBAL = "GLOBAL";
const PRIORITY_PILL: Record<string, string> = {
  Mandatory:
    "border-[rgba(212,53,60,0.25)] bg-[rgba(212,53,60,0.07)] text-[#a3262c]",
  Recommended:
    "border-[rgba(255,200,61,0.6)] bg-[rgba(255,200,61,0.16)] text-[#7a5600]",
  Optional: "border-[#e1e5eb] bg-[#f1f3f6] text-[var(--exed-ink-500)]",
};
export interface ActivitySelection {
  isChecked: (code: string) => boolean;
  allState: boolean | "indeterminate";
  onToggle: (codes: string[], checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}
export function UnsavedSection({
  items,
  children,
  footer,
}: {
  items: ChangedItem[];
  children: (item: ChangedItem) => ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  if (!items.length && !footer) return null;
  return (
    <section
      aria-label={t("Not saved")}
      className="border-b bg-[rgba(255,200,61,0.06)]"
    >
      <h4 className="px-3.5 py-2 text-xs font-semibold text-[#7a5600]">
        {t("Not saved")}
      </h4>
      <ul>
        {items.map((item) => (
          <Fragment key={item.key}>{children(item)}</Fragment>
        ))}
      </ul>
      {footer}
    </section>
  );
}
export interface ActivityDecisionListProps {
  kind?: ActivityKind;
  rows: ConfigActivityRow[];
  unsaved?: ChangedItem[];
  filters: ActivityFiltersState;
  appliedQ?: string;
  onFiltersChange: (filters: ActivityFiltersState) => void;
  onSearchEnter?: () => void;
  countries?: string[];
  totals: { areas: number; codes: number };
  selection?: ActivitySelection;
  onVerdict?: (codes: string[], verdict: "approved" | "out_of_scope") => void;
  onDetails: (row: ConfigActivityRow) => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  tree?: ReactNode;
  unsavedFooter?: ReactNode;
  pagingError?: ReactNode;
}
export function ActivityDecisionList({
  kind = "sscui",
  rows,
  unsaved = [],
  filters,
  onFiltersChange,
  onSearchEnter,
  countries = [],
  totals,
  selection,
  onVerdict,
  onDetails,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  tree,
  unsavedFooter,
  pagingError,
}: ActivityDecisionListProps) {
  const { t, i18n } = useTranslation();
  const set = (patch: Partial<ActivityFiltersState>) =>
    onFiltersChange({ ...filters, ...patch });
  return (
    <div>
      <div className={FILTER_ROW_CLASS}>
        <FilterSearch
          value={filters.q}
          onChange={(q) => set({ q })}
          label={t(
            kind === "sscui"
              ? "Search activity ID or name"
              : "Search ID or name",
          )}
          onEnter={onSearchEnter}
        />
        {kind === "sscui" && (
          <>
            <select
              aria-label={t("Priority")}
              value={filters.priority}
              onChange={(e) => set({ priority: e.target.value })}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">{t("Any priority")}</option>
              {["Mandatory", "Recommended", "Optional"].map((value) => (
                <option key={value} value={value}>
                  {t(value)}
                </option>
              ))}
            </select>
            <select
              aria-label={t("Country/Region")}
              value={filters.country}
              onChange={(e) => set({ country: e.target.value })}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">{t("All countries/regions")}</option>
              <option value={GLOBAL}>{t("Global")}</option>
              {countries.map((code) => (
                <option key={code} value={code}>
                  {countryName(code, i18n.language)}
                </option>
              ))}
            </select>
          </>
        )}
        <span className="ml-auto text-xs">
          {kind === "fiori"
            ? t("{{count}} configurations", { count: totals.codes })
            : t("{{areas}} application areas · {{activities}} activities", {
                areas: totals.areas,
                activities: totals.codes,
              })}
        </span>
      </div>
      {selection && (
        <SelectAllBar
          checked={selection.allState}
          shown={rows.length}
          onChange={selection.onToggleAll}
        />
      )}
      <UnsavedSection items={unsaved} footer={unsavedFooter}>
        {(item) => (
          <UnsavedPendingLine
            item={item}
            checked={selection?.isChecked(item.key) ?? false}
            onToggle={selection?.onToggle}
            onVerdict={onVerdict}
          />
        )}
      </UnsavedSection>
      {tree ?? (
        <>
          <ul data-testid="flat-activity-list">
            {rows.map((row) => (
              <ActivityLine
                key={row.activity_code}
                row={row}
                scopeId={null}
                checked={selection?.isChecked(row.activity_code) ?? false}
                onToggle={selection?.onToggle}
                onVerdict={onVerdict}
                onDetails={onDetails}
              />
            ))}
          </ul>
          {!rows.length && !unsaved.length && !hasMore && (
            <p className="px-5 py-10 text-center text-sm">
              {t(
                kind === "sscui"
                  ? "No pending activities match the filters."
                  : "No pending configurations match the filters.",
              )}
            </p>
          )}
          {pagingError}
          {onLoadMore && (
            <LoadMore
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={onLoadMore}
            />
          )}
        </>
      )}
    </div>
  );
}
/** Linha de uma configuração pendente. `scopeId` nulo é a linha da lista plana da App Fiori. */
export function ActivityLine({
  row,
  scopeId,
  checked,
  onToggle,
  onVerdict,
  onDetails,
}: {
  row: ConfigActivityRow;
  scopeId: string | null;
  checked: boolean;
  onToggle?: (codes: string[], checked: boolean) => void;
  onVerdict?: (codes: string[], verdict: "approved" | "out_of_scope") => void;
  onDetails: (row: ConfigActivityRow) => void;
}) {
  const { t } = useTranslation();
  const code = row.activity_code;
  return (
    <li
      data-testid={
        scopeId === null ? `config-${code}` : `config-${scopeId}-${code}`
      }
      data-activity-code={code}
      className={cn(
        "flex items-center gap-3 border-t border-[#f0f2f5] py-2.5 pr-3.5",
        scopeId === null ? "pl-3.5" : "pl-[46px]",
        checked ? "bg-[rgba(255,200,61,0.08)]" : "bg-white hover:bg-[#fafbfc]",
      )}
    >
      {onToggle && (
        <DecisionCheckbox
          checked={checked}
          onChange={(value) => onToggle([code], value)}
          label={t("Select {{name}}", { name: code })}
        />
      )}
      <span className="min-w-[56px] shrink-0 font-mono text-xs text-[var(--exed-ink-500)]">
        {code}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
        <span className="text-sm font-medium text-[var(--exed-ink-900)]">
          {row.name ?? code}
        </span>
        {row.priority && (
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.08em]",
              PRIORITY_PILL[row.priority] ?? PRIORITY_PILL.Optional,
            )}
          >
            {t(row.priority)}
          </span>
        )}
        {scopeId === null && row.application_area && (
          <span className="text-xs text-[var(--exed-ink-500)]">
            {row.application_area}
          </span>
        )}
        {scopeId === null && row.replicated_in > 0 && (
          <span className="text-xs text-[var(--exed-ink-500)]">
            {t("{{count}} scope items", { count: row.replicated_in })}
          </span>
        )}
        {/* Sem extração dá para decidir, mas não há detalhe nem execução (spec 2026-10-29). */}
        {row.extracted === false && (
          <span className="rounded-full border border-[#e1e5eb] bg-[#f7f7f7] px-2 py-0.5 text-[11px] font-medium text-[var(--exed-ink-500)]">
            {t("Not extracted")}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {onVerdict && (
          <ApproveButton
            data-pending-action=""
            label={t("Approve {{code}}", { code })}
            onClick={() => onVerdict([code], "approved")}
          />
        )}
        {onVerdict && (
          <OutOfScopeButton
            label={t("Take {{code}} out of scope", { code })}
            onClick={() => onVerdict([code], "out_of_scope")}
          />
        )}
        <span
          aria-hidden="true"
          className="mx-1 h-5 w-px bg-[var(--exed-card-border)]"
        />
        {/* Gerenciamento só decide: o atalho para abrir a SSCUI no SAP fica para a Execução. */}
        <GhostIconButton
          label={t("Details of {{code}}", { code })}
          title={
            row.extracted === false
              ? t("No extraction to show")
              : t("View details")
          }
          icon={<PanelRightOpen />}
          onClick={() => onDetails(row)}
          disabled={row.extracted === false}
          className="disabled:cursor-not-allowed disabled:opacity-40"
        />
      </div>
    </li>
  );
}

export function UnsavedPendingLine({
  item,
  checked,
  onToggle,
  onVerdict,
}: {
  item: ChangedItem;
  checked: boolean;
  onToggle?: (codes: string[], checked: boolean) => void;
  onVerdict?: (codes: string[], verdict: "approved" | "out_of_scope") => void;
}) {
  const { t } = useTranslation();
  const code = item.code;
  return (
    <li
      data-testid={`unsaved-${code}`}
      data-activity-code={code}
      className="flex items-center gap-3 border-t border-[#f3ead2] py-2 pl-3.5 pr-3.5"
    >
      {onToggle && (
        <DecisionCheckbox
          checked={checked}
          onChange={(value) => onToggle([code], value)}
          label={t("Select {{name}}", { name: code })}
        />
      )}
      <span className="min-w-[56px] shrink-0 font-mono text-xs text-[var(--exed-ink-500)]">
        {code}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--exed-ink-900)]">
        {item.name ?? code}
      </span>
      {onVerdict && (
        <ApproveButton
          data-pending-action=""
          label={t("Approve {{code}}", { code })}
          onClick={() => onVerdict([code], "approved")}
        />
      )}
      {onVerdict && (
        <OutOfScopeButton
          label={t("Take {{code}} out of scope", { code })}
          onClick={() => onVerdict([code], "out_of_scope")}
        />
      )}
    </li>
  );
}

export function ActivityDecidedList({
  rows,
  unsaved = [],
  search,
  onSearch,
  onSearchEnter,
  searchPlaceholder,
  total,
  onReturn,
  onDetails,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  tree,
  unsavedFooter,
  pagingError,
}: {
  kind?: ActivityKind;
  rows: ConfigActivityRow[];
  unsaved?: ChangedItem[];
  search: string;
  appliedSearch?: string;
  onSearch: (q: string) => void;
  onSearchEnter?: () => void;
  searchPlaceholder?: string;
  total: number;
  onReturn?: (code: string) => void;
  onDetails: (row: ConfigActivityRow) => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  tree?: ReactNode;
  unsavedFooter?: ReactNode;
  pagingError?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <DecidedPanelBody
      bare
      search={search}
      onSearch={onSearch}
      onSearchEnter={onSearchEnter}
      placeholder={searchPlaceholder ?? t("Search decided items")}
      searchLabel={t("Search decided items")}
      hasItems={total > 0 || !!unsaved.length || !!search}
      hasMatches={!!tree || !!rows.length || !!unsaved.length || hasMore}
    >
      <UnsavedSection items={unsaved} footer={unsavedFooter}>
        {(item) => (
          <DecidedRow
            code={item.code}
            name={item.name ?? item.code}
            rowProps={{ "data-testid": `unsaved-${item.code}` }}
            returnLabel={t("Return {{code}} to pending", { code: item.code })}
            onReturn={onReturn ? () => onReturn(item.code) : undefined}
            detailsLabel={t("Details of {{code}}", { code: item.code })}
          />
        )}
      </UnsavedSection>
      {tree ?? (
        <ul data-testid="flat-decided-list">
          {rows.map((row) => (
            <ActivityDecidedLine
              key={row.activity_code}
              row={row}
              onReturn={onReturn}
              onDetails={onDetails}
            />
          ))}
        </ul>
      )}
      {pagingError}
      {onLoadMore && (
        <LoadMore
          hasMore={hasMore}
          loading={loadingMore}
          onLoadMore={onLoadMore}
        />
      )}
    </DecidedPanelBody>
  );
}
export function ActivityDecidedLine({
  row,
  onReturn,
  onDetails,
}: {
  row: ConfigActivityRow;
  onReturn?: (code: string) => void;
  onDetails: (row: ConfigActivityRow) => void;
}) {
  const { t } = useTranslation();
  const code = row.activity_code;
  return (
    <DecidedRow
      code={code}
      name={row.name ?? code}
      rowProps={{
        "data-testid": `decided-${code}`,
        "data-activity-code": code,
      }}
      doneLabel={activityDoneLabel(row, t)}
      returnLabel={t("Return {{code}} to pending", { code })}
      onReturn={onReturn ? () => onReturn(code) : undefined}
      detailsLabel={t("Details of {{code}}", { code })}
      onDetails={() => onDetails(row)}
      detailsDisabledReason={
        row.extracted === false ? t("No extraction to show") : undefined
      }
    />
  );
}
